import EditIcon from 'core/icons/edit';
import IconButton from '@mui/material/IconButton';
import ToolTip from 'core/components/Tooltip';
import React, {
  useMemo,
} from 'react';
import DeleteIcon from 'core/icons/delete';
import InfoRow from 'views/EMR/components/Insurance/InsuranceCard/InfoRow';
import Select from 'core/components/Select';
import clsx from 'clsx';
import CircleIcon from '@mui/icons-material/CircleRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import LocalPoliceIcon from '@mui/icons-material/LocalPolice';
import {
  InsuranceSlot,
} from 'dtos/insuranceSlot';
import {
  InsuranceDTO,
} from 'dtos/insuranceDTO';
import isNil from 'lodash/isNil';
import {
  getAddressString,
} from 'utils/string';
import {
  getInsuranceBasicInfo,
} from 'utils/misc';
import Spinner from 'pages/Dashboard/components/Spinner';
import MenuItem from '@mui/material/MenuItem';
import {
  useInsuranceMetaData,
} from 'views/EMR/components/Insurance/hooks';

type Props = {
  insurance: InsuranceDTO;
  insuranceStatus: string;
  onInsuranceSlotChanged: (insuranceSlot: InsuranceSlot) => void;
  onActiveToggled: (isActive: boolean) => void;
  updatingInsuranceSlot?: boolean;
  updatingActiveStatus?: boolean;
  onOpenInsuranceDialog: (insurance?: InsuranceDTO) => void;
  onDeleteInsurance: (insurance?: InsuranceDTO) => void;
  isDeleting?: boolean;
};

export default function InsuranceHeader({
  insurance,
  insuranceStatus,
  onInsuranceSlotChanged,
  onActiveToggled,
  updatingInsuranceSlot,
  updatingActiveStatus,
  onOpenInsuranceDialog,
  onDeleteInsurance,
  isDeleting,
}: Props) {
  const {
    insuranceSlotValues,
    getInsuranceSlotLabel,
  } = useInsuranceMetaData();

  const basicInfo = useMemo(
    () => getInsuranceBasicInfo(insurance?.payerEntry ?? {}),
    [insurance?.payerEntry],
  );
  const statusOptions = ['Active', 'Inactive'];

  return (
    <div className="flex justify-between bg-blue-50 px-4 py-2 rounded-t-md border-b border-blue-100 min-h-[80px]">
      <div className="flex flex-col gap-1">
        <div className="text-base font-semibold">
          {insurance?.insuranceName}
        </div>
        <div className="text-xs text-gray-800">
          <ToolTip title={(
            <div className="flex flex-col gap-1 py-2 px-4">
              <InfoRow label="Payer ID" value={insurance?.payerEntry?.payerId} />
              <InfoRow label="Insurance Type" value={insurance?.payerEntry?.insuranceType} />
              <InfoRow label="Is Paper" value={insurance?.payerEntry?.isPaper ? 'Yes' : 'No'} />
            </div>
            )}
          >
            <span className="text-xs text-gray-800">
              {basicInfo}
            </span>
          </ToolTip>
        </div>
        {!isNil(insurance?.payerEntry?.address) && (
          <div className="text-xs text-gray-800">
            {getAddressString(insurance?.payerEntry?.address)}
          </div>
        )}
      </div>
      <div className="flex flex-col justify-between items-end">
        <div className="flex">
          <ToolTip title="Edit Insurance">
            <IconButton
              size="small"
              color="info"
              className="nw-edit-insurance-button"
              onClick={() => onOpenInsuranceDialog(insurance)}
            >
              <EditIcon className="!w-5 !h-5 !text-gray-800" />
            </IconButton>
          </ToolTip>
          <ToolTip title="Delete Insurance Policy">
            <IconButton
              size="small"
              color="error"
              onClick={() => onDeleteInsurance(insurance)}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <Spinner size={16} />
              ) : (
                <DeleteIcon className="!w-5 !h-5 !text-gray-800" />
              )}
            </IconButton>
          </ToolTip>
        </div>
        <div className="flex gap-2 text-xs text-gray-600 w-full">
          <Select<InsuranceSlot>
            getValue={(option) => option}
            getLabel={(option) => getInsuranceSlotLabel(option)}
            options={insuranceSlotValues}
            placeholder="Slot"
            id="insurance-slot"
            muiClasses={{ select: '!py-[3px] !text-xs !font-medium' }}
            selectClassName="!h-[22px] insurance-slot-select min-w-[126px]"
            value={insurance?.insuranceSlot}
            disabled={updatingInsuranceSlot}
            onChange={(value) => {
              onInsuranceSlotChanged(value as InsuranceSlot);
            }}
            renderValue={(value) => (
              <div className={clsx('flex items-center gap-1.5 text-gray-800 !text-blue-600', {
                '!text-gray-400': insurance?.inactive,
              })}
              >
                {value === InsuranceSlot.Primary && <LocalPoliceIcon className="!w-4 !h-4" />}
                <span className="font-semibold">
                  {getInsuranceSlotLabel(value as InsuranceSlot)}
                </span>
              </div>
            )}
          >
            {(options, getValue, getLabel) => options.map((option) => {
              const isSelected = option === insurance?.insuranceSlot;
              return (
                <MenuItem
                  key={getValue(option)}
                  value={getValue(option)}
                  className={clsx(
                    '!mx-1 !my-0.5 !rounded-md !px-2.5 !py-2 !text-xs !bg-transparent',
                    'hover:!bg-blue-50 focus-visible:!bg-blue-50',
                    { '!bg-blue-100/70': isSelected },
                  )}
                >
                  <div className="flex w-full items-center justify-between gap-3">
                    <span className={clsx('text-gray-800', {
                      'font-semibold': isSelected,
                      'font-medium': !isSelected,
                    })}
                    >
                      {getLabel(option)}
                    </span>
                    {isSelected && <CheckRoundedIcon className="!h-4 !w-4 !text-slate-600" />}
                  </div>
                </MenuItem>
              );
            })}
          </Select>
          <Select<string>
            getValue={(option) => option}
            getLabel={(option) => option}
            options={statusOptions}
            placeholder="Select"
            id="insurance-status"
            muiClasses={{ select: '!py-[3px] !text-xs !font-semibold' }}
            selectClassName="!h-[22px] insurance-status-select"
            value={insuranceStatus}
            disabled={updatingActiveStatus}
            onChange={(value) => {
              onActiveToggled(value === 'Active');
            }}
            renderValue={(value) => (
              <div className={clsx('flex items-center gap-1 font-semibold', {
                'text-blue-500': value === 'Active',
                'text-red-600': value === 'Inactive',
              })}
              >
                {updatingActiveStatus ? (
                  <Spinner size={10} />
                ) : (
                  <CircleIcon className="!h-2.5 !w-2.5 !text-inherit" />
                )}
                {value}
              </div>
            )}
          >
            {(options, getValue) => options.map((option) => {
              const isActive = option === 'Active';
              const isSelected = option === insuranceStatus;
              return (
                <MenuItem
                  key={getValue(option)}
                  value={getValue(option)}
                  className={clsx(
                    '!mx-1 !my-0.5 !rounded-md !px-2.5 !py-2 !text-xs !bg-transparent',
                    'hover:!bg-gray-100 focus-visible:!bg-gray-100',
                    {
                      '!bg-blue-100/70': isSelected && isActive,
                      '!bg-red-100/80': isSelected && !isActive,
                    },
                  )}
                >
                  <div className={clsx('flex w-full items-center font-semibold', {
                    'text-blue-600': isActive,
                    'text-red-600': !isActive,
                  })}
                  >
                    {option}
                  </div>
                </MenuItem>
              );
            })}
          </Select>
        </div>
      </div>
    </div>
  );
}
